import { useContext, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Card,
  CardContent,
  Tooltip,
  Link,
  tooltipClasses,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { UserQuizSubmission } from "@backend-types";
import { useHistory } from "react-router-dom";
import { CourseStructureContext } from "../../../context/CourseStructureContext";
import { ComputedQuizScore, CourseStructureCtx, UserCtx } from "../../../interfaces";
import { Question, Target as TargetTree } from "../../utils/CourseTree";
import { TARGET_COMPLETION_STATUS } from "../../../enums";
import { MacsAuthenticator } from "../../../hooks/useMacsAuthenticator";
import { _AuthType } from "../../../enums";
import { UserContext } from "../../../context/UserContext";

const useStyles = makeStyles((theme) => ({
  cardContainer: {
    minWidth: "378px",
    minHeight: "104px",
    width: "100%",
    padding: "24px",
    transition: "box-shadow 200ms ease-in-out",
    borderColor: theme.palette.primary.main,
  },
  scoreIndicator: {
    padding: "4px 8px",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "24px",
    color: "#5d5d5d",
    backgroundColor: "#fff",
  },
  statusIndicator: {
    padding: "4px 8px",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "24px",
  },

  statusInProgress: {
    backgroundColor: "#FDEDF1",
    color: "#D81D4F",
  },
  statusNeedsPractice: {
    backgroundColor: theme.palette.warning.main,
  },
  statusMastered: {
    backgroundColor: "#E9F5DB",
    color: "#477213",
  },
  statusNotStarted: {
    backgroundColor: "#E8E8E8",
    color: "#484848",
  },
  lockStatusIndicator: {
    padding: "4px 8px",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "24px",
    backgroundColor: "#E8E8E8",
    color: "#484848",
    marginTop: "0px !important",
  },
}));

export interface DigitStudyToolProps {
  target: TargetTree;
  status: TARGET_COMPLETION_STATUS;
  targetQuizSubmissions: UserQuizSubmission[];
  resumeQuestion: Question | undefined;
  parentLockStatus?: boolean;
  emphasize?: boolean;
}

interface LessonProgressIndicatorProps {
  status: TARGET_COMPLETION_STATUS;
}

interface IProgressIndicator {
  class: string;
  text: string;
}

function LessonProgressIndicator(props: LessonProgressIndicatorProps) {
  const classes = useStyles();

  const getStatus = (): IProgressIndicator => {
    switch (props.status) {
      case TARGET_COMPLETION_STATUS.IN_PROGRESS:
        return { class: classes.statusInProgress, text: "IN PROGRESS" };
      case TARGET_COMPLETION_STATUS.MASTERED:
        return { class: classes.statusMastered, text: "COMPLETED" };
      case TARGET_COMPLETION_STATUS.NEEDS_PRACTICE:
        return { class: classes.statusNeedsPractice, text: "NEEDS PRACTICE" };
      case TARGET_COMPLETION_STATUS.NOT_STARTED:
      default:
        return { class: classes.statusNotStarted, text: "NOT STARTED" };
    }
  };

  const status = getStatus();

  return (
    <Box className={`${classes.statusIndicator} ${status.class}`}>
      <Typography variant="body1" lineHeight="1em" tabIndex={0}>
        {status.text}
      </Typography>
    </Box>
  );
}
export function DigitStudyToolLessonCard(props: DigitStudyToolProps) {
  const { target, status, resumeQuestion, emphasize } = props;
  const classes = useStyles();
  const history = useHistory();
  const userCtx = useContext(UserContext) as UserCtx;
  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;
  const currentTargetName = target.getFormattedTargetName();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [isTargetPressed, setIsTargetPressed] = useState(false);
  const lockIconRef = useRef(null);

  const quizScore = useMemo<ComputedQuizScore>(() => {
    return userCtx.calculateQuizScore([target]);
  }, [target, userCtx]);

  const handleStart = () => {
    if (!resumeQuestion) return;
    courseStructureCtx.setBackBehavior({ backTo: "moduleoverview" });
    history.push({
      pathname: "/solver",
      search: "&questionId=" + resumeQuestion.id,
    });
  };

  const scoreDisplay = () => {
    switch (quizScore?.invalid) {
      case "No Quiz":
        return "No Quiz";
      case "-":
        return "-";
      case undefined:
      default:
        return quizScore.display_as === "percentage"
          ? `${Math.round((quizScore.percentage ?? 0) * 100)}%`
          : quizScore.display_as === "points"
          ? `${quizScore.points ?? 0}/${quizScore.max_points ?? 0}`
          : "-";
    }
  };

  const currentCourse = courseStructureCtx.courseTreeStructure?.getCurrentCourse();

  const courseMetadata = currentCourse?.metadata;

  return (
    <Card
      component="button"
      type="button"
      tabIndex={0}
      className={classes.cardContainer}
      onMouseDown={() => setIsTargetPressed(true)}
      onMouseUp={() => setIsTargetPressed(false)}
      onMouseLeave={() => setIsTargetPressed(false)}
      onClick={handleStart}
      disabled={target.is_asset_locked}
      aria-label={currentTargetName}
      sx={{
        border:
          isTargetPressed || emphasize
            ? "2px solid #0092E0 !important"
            : "0px solid transparent",
        textAlign: "left",
        cursor: target.is_asset_locked ? "default" : "pointer",
        "&:hover": {
          backgroundColor: target.is_asset_locked ? "none" : "#E8E8E8",
        },
      }}>
      <CardContent
        sx={{
          height: "100%",
          padding: "0px !important",
          display: "flex",
          flexDirection: "column",
        }}
        onMouseEnter={() => target.is_asset_locked && setTooltipOpen(true)}
        onMouseLeave={() => setTooltipOpen(false)}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "16px",
            width: "100%",
            alignItems: "stretch",
          }}>
          <Stack
            direction="column"
            sx={{
              flexGrow: 1,
              overflow: "hidden",
            }}>
            <Stack
              display={"flex"}
              flexDirection={"row"}
              alignItems={"center"}
              spacing={2}
              sx={{ marginBottom: "16px" }}>
              <Typography
                fontSize="1.5em"
                lineHeight="36px"
                variant="h2"
                fontWeight={600}>
                {currentTargetName}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" marginTop="auto">
              <Stack direction="row" spacing={1}>
                {target.is_asset_locked && !props.parentLockStatus ? (
                  <Box ref={lockIconRef} className={classes.lockStatusIndicator}>
                    <Tooltip
                      open={tooltipOpen}
                      placement="bottom-start"
                      arrow
                      PopperProps={{
                        sx: {
                          // Shift the tooltip box
                          [`&.${tooltipClasses.popper} .${tooltipClasses.tooltip}`]: {
                            marginLeft: "-8px",
                          },
                          // Shift the arrow back 8px to the left to compensate
                          [`&.${tooltipClasses.popper} .${tooltipClasses.arrow}`]: {
                            marginRight: "-4px",
                            transform: "translate(8px, 0px) !important",
                          },
                          [`& .MuiTooltip-tooltip`]: {
                            maxWidth: "none",
                          },
                        },
                      }}
                      title={
                        <span>
                          Head back to{" "}
                          {MacsAuthenticator.authHook.authType === _AuthType.LTI &&
                          MacsAuthenticator.authHook.ltiReturnUrl ? (
                            <Link
                              href={MacsAuthenticator.authHook.ltiReturnUrl}
                              underline="always"
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                color: "white",
                                textDecorationColor: "white",
                                "&:hover": {
                                  color: "#0092E0",
                                  textDecorationColor: "#0092E0",
                                },
                              }}
                              onClick={(e) => e.stopPropagation()}>
                              Canvas
                            </Link>
                          ) : (
                            "Canvas"
                          )}{" "}
                          to launch this submodule.
                        </span>
                      }>
                      <i className="fa-solid fa-lock" style={{ marginRight: "8px" }}></i>
                    </Tooltip>
                    <Typography variant="body1" lineHeight="1em">
                      LOCKED
                    </Typography>
                  </Box>
                ) : (
                  <LessonProgressIndicator status={status} />
                )}

                <Box className={`${classes.scoreIndicator}`}>
                  <Typography
                    variant="body1"
                    lineHeight="1em"
                    textTransform="uppercase"
                    tabIndex={0}>
                    Score: {scoreDisplay()}
                  </Typography>
                </Box>
                {!courseMetadata?.module_overview_page?.disable_try_again_icon &&
                  quizScore?.percentage !== null &&
                  quizScore?.percentage < 0.8 && (
                    <Box
                      sx={{
                        cursor: "pointer",
                        color: "#F96712",
                      }}>
                      <Tooltip placement="right" arrow title="Try again">
                        <i className="fa-solid fa-circle-exclamation"></i>
                      </Tooltip>
                    </Box>
                  )}
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
}
